import React, { useMemo, useEffect, useRef } from 'react';
import { Row, Col, Form, Card, Input } from 'antd';
import NormalForm from './NormalForm';
import DermatologyImage from './dermatology/DermatologyImage';
import DermatologyLegend from './dermatology/DermatologyLegend';
import DermatologySkinType from './dermatology/DermatologySkinType';
import { calculateCounts } from './dermatology/dermatologyUtils';

const DermatologyForm = ({
  inputs,
  disableForm,
  readOnly = false,
  form,
  roles,
  fileList,
  loadingFiles,
  status,
  setFileList,
  toDoctor,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  countrySettings,
  fromExternal,
  dermatologyState,
  setDermatologyState,
  setDermatologyChartRef,
}) => {
  const isInitialized = useRef(false);
  const { marks, otherText } = dermatologyState;

  const setMarks = (newMarks) => setDermatologyState(prev => ({ ...prev, marks: newMarks }));
  const setOtherText = (text) => setDermatologyState(prev => ({ ...prev, otherText: text }));

  const isDoctor = roles.includes('doctor') || roles.includes('direccion_medica');
  const isPsychologist = roles.includes('equipo_psicologia');

  const disableCondition = readOnly || !((isDoctor && toDoctor) || (!disableForm && !isDoctor) || (!disableForm && isPsychologist));

  const getDisableCondition = (item) => {
    return readOnly || (disableCondition && !(isDoctor && item?.medical_visibility));
  };

  const workerSurveyCode = 'PARA_TRABAJADOR_SCREENING_DERMATOLOGICO';
  const facialImageSurveyCode = 'IMAGEN_FACIAL_SCREENING_DERMATOLOGICO';
  const otrasDetailQuestionCode = 'OTRAS_MARCAS_DETALLE_IMAGEN_FACIAL';
  const fitzpatrickSurveyCode = 'FOTOTIPO_PIEL_SCREENING_DERMATOLOGICO';
  const fitzpatrickQuestionCode = 'FITZPATRICK_FOTOTIPO_PIEL';

  const countProcedures = useMemo(() => {
    const countsCodes = ['COUNT_MOLES', 'COUNT_FRECKLES', 'COUNT_SPOTS', 'COUNT_SCARS', 'COUNT_OTHERS'];
    return inputs?.procedures?.filter(procedure => countsCodes.includes(procedure.code)) || [];
  }, [inputs?.procedures]);

  const otherQuestion = useMemo(() => {
    return inputs?.questions?.find(question => question.code === otrasDetailQuestionCode);
  }, [inputs?.questions, otrasDetailQuestionCode]);

  const fitzpatrickQuestion = useMemo(() => {
    return inputs?.questions?.find(question => question.code === fitzpatrickQuestionCode);
  }, [inputs?.questions, fitzpatrickQuestionCode]);

  const fitzpatrickSurvey = useMemo(() => {
    return inputs.surveys?.find(survey => survey.code === fitzpatrickSurveyCode);
  }, [inputs.surveys, fitzpatrickSurveyCode]);

  const fitzpatrickObservationsQuestion = useMemo(() => {
    return inputs?.questions?.find(question => 
      question.survey_id === fitzpatrickSurvey?.id && 
      question.id !== fitzpatrickQuestion?.id
    );
  }, [inputs?.questions, fitzpatrickSurvey, fitzpatrickQuestion]);

  useEffect(() => {
    if (isInitialized.current) return;

    const savedData = inputs.extra_data?.facialExamMarks;
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setMarks(parsedData.marks || []);
      } catch (error) {
        console.error("Error parsing dermatology marks:", error);
      }
    }

    if (otherQuestion?.result) {
      setOtherText(otherQuestion.result);
    }

    isInitialized.current = true;
  }, [inputs.extra_data, otherQuestion, setMarks, setOtherText]);

  useEffect(() => {
    if (!isInitialized.current) return;

    const currentCounts = calculateCounts(marks);
    const syncedFormValues = {
      procedures: { ...(form.getFieldValue('procedures') || {}) },
    };

    if (otherQuestion) {
      syncedFormValues.questions = {
        ...(form.getFieldValue('questions') || {}),
        [otherQuestion.id.toString()]: otherText,
      };
    }

    countProcedures.forEach(procedure => {
      syncedFormValues.procedures[procedure.id.toString()] = currentCounts[procedure.code] || 0;
    });

    form.setFieldsValue(syncedFormValues);
  }, [marks, otherText, countProcedures, otherQuestion, form]);

  const workerInputs = useMemo(() => {
    const workerSurvey = inputs.surveys?.find(survey => survey.code === workerSurveyCode);
    return {
      ...inputs,
      surveys: workerSurvey ? [workerSurvey] : [],
      questions: inputs.questions?.filter(question => question.survey_id === workerSurvey?.id),
      procedures: inputs.procedures?.filter(procedure =>
        !procedure.code?.startsWith('COUNT_')
      ),
    };
  }, [inputs]);

  const remainingInputs = useMemo(() => {
    const workerSurvey = inputs.surveys?.find(survey => survey.code === workerSurveyCode);
    const facialImageSurvey = inputs.surveys?.find(survey => survey.code === facialImageSurveyCode);
    const fitzpatrickSurvey = inputs.surveys?.find(survey => survey.code === fitzpatrickSurveyCode);

    const surveyIdsToHide = [workerSurvey?.id, facialImageSurvey?.id, fitzpatrickSurvey?.id].filter(Boolean);
    const codesToHide = ['COUNT_MOLES', 'COUNT_FRECKLES', 'COUNT_SPOTS', 'COUNT_SCARS', 'COUNT_OTHERS'];

    return {
      ...inputs,
      surveys: inputs.surveys?.filter(survey => !surveyIdsToHide.includes(survey.id)),
      questions: inputs.questions?.filter(question => !surveyIdsToHide.includes(question.survey_id)),
      procedures: inputs.procedures?.filter(procedure => !codesToHide.includes(procedure.code)),
      measurements: inputs.measurements?.filter(measurement => !surveyIdsToHide.includes(measurement.survey_id)),
    };
  }, [inputs, workerSurveyCode, facialImageSurveyCode, fitzpatrickSurveyCode]);

  const commonProps = {
    disableForm, roles, status, loadingFiles, setFileList, fileList,
    toDoctor, removeDocument, convertFile, convertError,
    isDICOM, handleConvertFile, imageUrl, setImageUrl, countrySettings, form,
    fromExternal, fullInputs: inputs
  };

  return (
    <div className="dermatology-form-wrapper mt-20">
      <NormalForm {...commonProps} inputs={workerInputs} />

      <div className="dermatology-interactive-wrapper-parent mt-60 mb-40">
        <Col lg={24} xs={24} className="mb-24">
          <h2><strong>Imagen Facial</strong></h2>
        </Col>
        <Card className="form-section-card mb-32" bordered={true}>
          <div className="dermatology-section-description mb-20">
            Haga clic sobre la imagen para colocar marcas. Seleccione el tipo de marca antes de hacer clic.
          </div>
          <DermatologyImage
            marks={marks}
            onMarksChange={setMarks}
            disabled={disableCondition}
            setDermatologyChartRef={setDermatologyChartRef}
            fromExternal={fromExternal}
          />
          <DermatologyLegend
            marks={marks}
            disabled={disableCondition}
            otherText={otherText}
            onOtherTextChange={setOtherText}
            otherQuestion={otherQuestion}
          />
        </Card>
      </div>

      {(fitzpatrickQuestion || fitzpatrickObservationsQuestion) && (
        <div className="mt-60 mb-40">
          <Col lg={24} xs={24} className="mb-24">
            <h2><strong>{fitzpatrickSurvey?.name || 'Fototipo de Piel (Fitzpatrick)'}</strong></h2>
          </Col>
          <Card className="form-section-card mb-32" bordered={true}>
            {fitzpatrickQuestion && (
              <Form.Item
                label={fitzpatrickQuestion.name}
                name={['questions', fitzpatrickQuestion.id.toString()]}
                className="mb-24"
                initialValue={fitzpatrickQuestion.result || fitzpatrickQuestion.default_value}
              >
                <DermatologySkinType question={fitzpatrickQuestion} disabled={getDisableCondition(fitzpatrickQuestion)} />
              </Form.Item>
            )}

            {fitzpatrickObservationsQuestion && (
              <Form.Item
                label={fitzpatrickObservationsQuestion.name}
                name={['questions', fitzpatrickObservationsQuestion.id.toString()]}
                initialValue={fitzpatrickObservationsQuestion.result || fitzpatrickObservationsQuestion.default_value}
              >
                <Input disabled={getDisableCondition(fitzpatrickObservationsQuestion)} className="cmt-input mt-10" placeholder="Ingrese observaciones" />
              </Form.Item>
            )}
          </Card>
        </div>
      )}

      <NormalForm {...commonProps} inputs={remainingInputs} />

      {countProcedures.map(procedure => (
        <Form.Item key={procedure.id} name={['procedures', procedure.id.toString()]} noStyle>
          <input type="hidden" />
        </Form.Item>
      ))}
    </div>
  );
};

export default DermatologyForm;
